import React, { useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Button
} from '@material-ui/core';
import { DialogWithFocusProvider } from 'components/layout';
import ClearIcon from '@material-ui/icons/Clear';
import debounce from 'lodash/debounce';
import { Resizable } from 're-resizable';
import { LoadingOverlayWrapper} from 'components/layout';
import { PostAcqUWContent, PostAcqUWProforma } from 'components/underwrite/postAcquisition/postAcqUnderwrite';
import {
  setProformaWidth, takePostAcqValuation, appNotify
} from 'store/actions';
import { parseISO, format } from 'date-fns';
import classNames from 'classnames';
import { useSaveUWDialog } from 'components/underwrite/shared/SaveUWDialog';

const PostAcqViewUnderwriteModal = ({ classes, open, handleClose, proformaWidth, setProformaWidth,
  selectedOpportunity, selectedContainer, valuationIsDirty, appNotify, isFetchingPostAcqUWData, viewUnderwriteModalIsOpen,
  modalSelectedValuation, takePostAcqValuation
}) => {
  const { openSaveUWDialog } = useSaveUWDialog();

  useEffect(() => {
    if (!open) return;
    let abortController = new AbortController();

    return () => {
      abortController.abort();
    }
  }, [open]);

  const onResize = debounce(() => {
    setProformaWidth(proformaResizable.resizable.clientWidth);
  }, 100);

  const onClose = () => {
    handleClose();
  }

  const onTakeButtonClick = (e) => {
    if (valuationIsDirty) {
      openSaveUWDialog({ callback: () => {
        takePostAcqValuation(modalSelectedValuation);
      }});
    } else {
      takePostAcqValuation(modalSelectedValuation);
    }
  }
  let proformaResizable;  // Ref to resizable component, used to manually update size
  const hasSavedValuation = !!modalSelectedValuation?.id;
  return (
    <DialogWithFocusProvider onClose={onClose} open={open} classes={{ root: classes.dialog, paper: classNames(classes.dialogPaper, viewUnderwriteModalIsOpen && classes.padded) }} maxWidth={false} fullWidth={true}>
      <DialogTitle>
        {selectedOpportunity
          ? <div style={{ display: 'flex' }}>
              <span>
                <>{`${selectedOpportunity.requestType ?? ''} `}<code>{selectedOpportunity.id}</code>{selectedOpportunity.postAcquisitionValuationCreatedOn ? ' - ' + format(parseISO(selectedOpportunity.postAcquisitionValuationCreatedOn), 'MM/dd/yyyy') : ''}</>
              </span>
              <span style={{ flexGrow: 1 }}/>
              {hasSavedValuation && (
                <Button variant='contained' color='primary' onClick={onTakeButtonClick} disabled={isFetchingPostAcqUWData} style={{ marginRight: 32 }}>Take</Button>
              )}
            </div>
          : <div style={{ height: 30 }}/>
        }
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={onClose}>
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent className={classes.dialogContent} dividers>
        <LoadingOverlayWrapper
          className={classes.loadingOverlay}
          active={selectedOpportunity?.isProcessing || isFetchingPostAcqUWData}
          message={selectedOpportunity?.isProcessing ? 'Processing data...' : isFetchingPostAcqUWData ? 'Fetching underwrite data...' : ''}
        >
          {selectedOpportunity && selectedContainer
            ? <div className={classes.container}>
                <PostAcqUWContent isUWModal={true} viewingReadonlyMode={true}/>
                {hasSavedValuation
                  ? <div>
                      <Resizable ref={r => { proformaResizable = r; }} onResize={onResize} minWidth='300' maxWidth='400'
                        size={{ width: `${proformaWidth}px`, height: ('100%') }}
                        enable={{ top: false, right: false, bottom: false, left: true, topRight: false, bottomRight: false, bottomLeft: false, topLeft: false }}>
                        <PostAcqUWProforma resizableWidth={proformaWidth} viewingReadonlyMode={true} handleModalClose={handleClose} />
                      </Resizable>
                    </div>
                  : <div><div className={classes.emptyProforma} style={{ width: proformaWidth }}>
                      <Typography variant='subtitle2'>No underwrite for this load</Typography>
                    </div></div>
                }
              </div>
            : <div className={classes.noUnderwriteContainer}>
                <Typography variant='subtitle2'>No underwrite found</Typography>
              </div>
          }
        </LoadingOverlayWrapper>
      </DialogContent>
    </DialogWithFocusProvider>
  )
}

const styles = theme => ({
  dialog: {
    minWidth: '200px',
    minHeight: '200px'
  },
  dialogPaper: {
    height: '100%'
  },
  dialogContent: {
    padding: 0
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  container: {
    display: 'flex',
    gridTemplateAreas: `'content proforma'`,
    gridTemplateColumns: '1fr auto',
    height: '100%',
    width: '100%'
  },
  loadingOverlay: {
    display: 'flex',
    width: '100%',
    height: '100%'
  },
  noUnderwriteContainer: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  },
  emptyProforma: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderLeft: '1px solid #e0e0e0',
    height: '100%',
    backgroundColor: 'white'
  },
  padded: {
    maxWidth: 'calc(100% - 128px)',
    maxHeight: 'calc(100% - 128px)'
  }
});

const mapState = ({ postAcqUWState, exploreMap }, ownProps) => {
  return {
    selectedOpportunity: postAcqUWState.underwriteModalState.postAcqUWClientTask,
    selectedContainer: postAcqUWState.underwriteModalState.selectedContainer,
    valuationIsDirty: postAcqUWState.valuationIsDirty,
    proformaWidth: postAcqUWState.proformaWidth,
    isFetchingPostAcqUWData: postAcqUWState.underwriteModalState.isFetchingPostAcqUWData,
    viewUnderwriteModalIsOpen: exploreMap.viewUnderwriteModalIsOpen,
    modalSelectedValuation: postAcqUWState.underwriteModalState.selectedValuation
  }
};
const mapDispatch = {
  setProformaWidth,
  appNotify,
  takePostAcqValuation
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(PostAcqViewUnderwriteModal);